import { CostCodePlanVsActual } from "models/expense-budget"
import { AlertThreshold, isOverThreshold, sumAmounts } from "utils/expense-budget"
import { formatCurrency } from "utils/tools"

interface MonthlyCostCodeRowsProps {
    costCode: CostCodePlanVsActual
    periods: number[]
    firstOpenPeriod: number
    threshold: AlertThreshold
}

const rowLabelStyle = { minWidth: 260, position: "sticky" as const, left: 0, background: "var(--bs-body-bg)" }
const amount = (value: number) => formatCurrency(value, "CAD", 0)

// One cost code block: plan, actual and variance rows, then the plan of each of its lines.
// The "Unmapped lines" group only has a plan: it has no GL account to pull actuals from.
const MonthlyCostCodeRows = ({ costCode, periods, firstOpenPeriod, threshold }: MonthlyCostCodeRowsProps) => {
    const isClosed = (period: number) => period < firstOpenPeriod
    const hasActuals = !costCode.isUnmapped
    const variance = periods.map((period, i) => (isClosed(period) ? costCode.actual[i] - costCode.planned[i] : null))

    const varianceStyle = (i: number) =>
        isClosed(periods[i]) && isOverThreshold(costCode.planned[i], costCode.actual[i], threshold)
            ? { background: "var(--bs-danger-bg-subtle)", color: "var(--bs-danger)", fontWeight: 600 }
            : undefined

    const title = costCode.isUnmapped ? "Unmapped lines" : `${costCode.code} — ${costCode.description}`

    return (
        <>
            <tr style={{ background: "var(--bs-secondary-bg)", fontWeight: 600 }}>
                <td style={{ ...rowLabelStyle, background: "var(--bs-secondary-bg)" }}>{title} · Plan</td>
                {costCode.planned.map((value, i) => <td key={i} className="text-end">{amount(value)}</td>)}
                <td className="text-end">{amount(sumAmounts(costCode.planned))}</td>
            </tr>
            {hasActuals && <>
                <tr>
                    <td style={rowLabelStyle}>Actual (GL)</td>
                    {costCode.actual.map((value, i) => (
                        <td key={i} className="text-end">{isClosed(periods[i]) ? amount(value) : "—"}</td>
                    ))}
                    <td className="text-end">{amount(sumAmounts(costCode.actual))}</td>
                </tr>
                <tr>
                    <td style={rowLabelStyle}>Variance</td>
                    {variance.map((value, i) => (
                        <td key={i} className="text-end" style={varianceStyle(i)}>{value === null ? "—" : amount(value)}</td>
                    ))}
                    <td className="text-end">Forecast {amount(costCode.forecast)}</td>
                </tr>
            </>}
            {costCode.lines.map(line => (
                <tr key={line.id} className="text-muted">
                    <td style={{ ...rowLabelStyle, paddingLeft: 24 }}>{line.vendorName} – {line.productName}</td>
                    {line.planned.map((value, i) => <td key={i} className="text-end">{value ? amount(value) : ""}</td>)}
                    <td className="text-end">{amount(sumAmounts(line.planned))}</td>
                </tr>
            ))}
        </>
    )
}

export default MonthlyCostCodeRows
